// Převzetí materiálu z počítárny — „Noční kokpit". První úkol trasy:
// řidič vidí předepsané pečetě JBO, nakládá bez podpisů — obrazovka jen
// informuje (naloženo / k naložení). Doručení na pobočce řeší detail
// zastávky (podpis přebírající osoby). API beze změny.

import { useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { IonContent, IonPage, IonSpinner } from "@ionic/react";
import { zasilkyMaterial, zasilkaSeals, type ZasilkaMaterial } from "../lib/api";
import { useMaterialStore } from "../store/materialStore";
import { ThemeToggle } from "../components/ThemeToggle";

export function MaterialScreen() {
  const history = useHistory();
  const qc = useQueryClient();
  const { setTotal } = useMaterialStore();

  const { data: material = [], isLoading } = useQuery<ZasilkaMaterial[]>({
    queryKey: ["mobile", "zasilky", "material"],
    queryFn: zasilkyMaterial.list,
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["mobile", "zasilky", "material"] });
  const nalozit = useMutation({ mutationFn: (id: string) => zasilkyMaterial.nalozeno(id), onSuccess: invalidate });
  const dorucit = useMutation({ mutationFn: (id: string) => zasilkyMaterial.doruceno(id), onSuccess: invalidate });

  const toLoad = material.filter((m) => m.stav === "vytvoreno");
  const toDeliver = material.filter((m) => m.stav === "predano");

  useEffect(() => { if (!isLoading) setTotal(toLoad.length); }, [isLoading, toLoad.length, setTotal]);

  const matCard = (m: ZasilkaMaterial, loaded: boolean) => {
    const seals = zasilkaSeals(m);
    return (
      <div className="card" key={m.id} style={{ marginTop: 12 }}>
        <div className="cap" style={{ display: "flex", marginBottom: 6 }}>
          {m.branch_id ?? ""} {(m.klient ?? "").toUpperCase()}
          <span className="spacer" />
          {loaded && <span style={{ color: "var(--ok)", letterSpacing: 0 }}>naloženo</span>}
        </div>
        {m.prijemce && <div style={{ fontSize: 12.5, color: "var(--soft)", marginBottom: 6 }}>{m.prijemce}</div>}
        {seals.length > 0 ? (
          <div className="mono" style={{ fontSize: 16, fontWeight: 700, color: "var(--ink)", letterSpacing: ".04em" }}>
            {seals.join(" · ")}
          </div>
        ) : (
          <div style={{ fontSize: 12.5, color: "var(--dim)" }}>bez pečetí</div>
        )}
        {!loaded ? (
          <button
            className="cta"
            style={{ marginTop: 12, padding: 14, fontSize: 15 }}
            disabled={nalozit.isPending}
            onClick={() => nalozit.mutate(m.id)}
          >
            {nalozit.isPending ? "Odesílám…" : "Naloženo"}
          </button>
        ) : (
          <button
            className="cta quiet"
            style={{ marginTop: 12, padding: 12, fontSize: 14 }}
            disabled={dorucit.isPending}
            onClick={() => dorucit.mutate(m.id)}
          >
            Doručit bez podpisu (nouzově)
          </button>
        )}
      </div>
    );
  };

  return (
    <IonPage>
      <IonContent scrollY={false}>
        <div className="ck scenebg" style={{ height: "100%" }}>
          <div className="top">
            <button className="back" onClick={() => history.goBack()}>‹</button>
            <div style={{ flex: 1, minWidth: 0 }}>
              <b style={{ fontSize: 19, display: "block" }}>Převzít materiál</b>
              <span style={{ display: "block", fontSize: 13.5, color: "var(--soft)" }}>počítárna C.I.T</span>
            </div>
            <ThemeToggle />
          </div>

          <div className="stopbody"><div className="pad-x">
            {isLoading ? (
              <div style={{ textAlign: "center", padding: 40 }}><IonSpinner name="crescent" /></div>
            ) : material.length === 0 ? (
              <div style={{ padding: "26px 0", color: "var(--dim)", fontSize: 14, textAlign: "center" }}>
                Počítárna nepřiřadila žádný materiál pro tvoji trasu.
              </div>
            ) : (
              <>
                {toLoad.length > 0 && (
                  <>
                    <div className="cap" style={{ marginTop: 14, marginBottom: 0, display: "flex" }}>
                      K naložení
                      <span className="spacer" />
                      <span className="mono" style={{ color: "var(--ink)", letterSpacing: 0, fontSize: 14 }}>{toLoad.length}</span>
                    </div>
                    {toLoad.map((m) => matCard(m, false))}
                  </>
                )}
                {toDeliver.length > 0 && (
                  <>
                    <div className="cap" style={{ marginTop: 18, marginBottom: 0, display: "flex" }}>
                      Naloženo — doručí se na pobočkách
                      <span className="spacer" />
                      <span className="mono" style={{ color: "var(--ink)", letterSpacing: 0, fontSize: 14 }}>{toDeliver.length}</span>
                    </div>
                    {toDeliver.map((m) => matCard(m, true))}
                  </>
                )}
                <div className={`stop-why ${toLoad.length === 0 ? "ok" : "warn"}`} style={{ marginTop: 16 }}>
                  {toLoad.length === 0
                    ? "Vše naloženo"
                    : toLoad.length === 1 ? "Zbývá naložit 1 zásilku" : `Zbývá naložit ${toLoad.length} zásilky`}
                </div>
                {(nalozit.isError || dorucit.isError) && (
                  <div style={{ marginTop: 8, fontSize: 12.5, color: "var(--bad)", textAlign: "center" }}>
                    Chyba: {String(((nalozit.error || dorucit.error) as Error)?.message)}
                  </div>
                )}
              </>
            )}
          </div></div>

          <div className="bottom">
            <button className="cta" onClick={() => history.goBack()}>
              {toLoad.length === 0 ? "Hotovo — zpět" : "Zpět"}
            </button>
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
